import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';
import Spinner from '../spinner'; 
import { XMarkIcon } from '@heroicons/react/24/outline';

const ClientModal = ({ isOpen, setIsOpen, selectClient, value, isSelectable = true, useButton = true, clients = [] }) => {
    const [availableClients, setAvailableClients] = useState(clients);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);

    const fetchClients = useCallback(async (query) => {
        setIsLoading(true);
        try {
            const apiToken = localStorage.getItem('accessToken');
            const apiUrl = process.env.REACT_APP_API_URL;
            const params = query ? { name: query } : {};
    
            const response = await axios.get(`${apiUrl}/clients`, {
                headers: getHeaders(apiToken),
                params,
            });
            setAvailableClients(response.data.data);
        } catch (error) {
            console.error('Error fetching clients', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSelectedClient(value);
            return;
        }
        if (initialLoad) {
            if (clients && clients.length > 0) {
                setAvailableClients(clients);
            } else {
                fetchClients('');
            }
            setInitialLoad(false);
        }
    }, [isOpen, value, clients, initialLoad, fetchClients]);

    useEffect(() => {
        if (!isOpen) return;
        if (searchQuery) {
            const debounceTimer = setTimeout(() => {
                fetchClients(searchQuery);
            }, 500);
            return () => clearTimeout(debounceTimer);
        }
    }, [isOpen, searchQuery, fetchClients]);

    const toggleModal = () => {
        setIsOpen(!isOpen);
    };

    const handleSelectClient = (client) => {
        selectClient(client);
        setSelectedClient(client);
        setIsOpen(false);
    };

    const handleClearClient = () => {
        selectClient(null);
        setSelectedClient(null);
    };

    return (
        <>
            <div className="flex items-end space-x-4 mb-4">
                <div className="flex-1 relative">
                    <label htmlFor="client_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Provider Selected</label>
                    <input
                        type="text"
                        id="client_id"
                        name="client_id"
                        value={selectedClient ? `${selectedClient.name}` : ''}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 min-w-[220px]"
                        placeholder="Provider"
                        readOnly
                        onClick={!useButton ? toggleModal : undefined}
                        style={{ cursor: !useButton ? 'pointer' : 'default' }}
                    />
                    {(selectedClient && isSelectable) && (
                        <button
                            onClick={handleClearClient}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 mt-7"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    )}
                </div>
                {useButton && isSelectable && (
                    <div>
                        <button onClick={toggleModal} className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                            Select
                        </button>
                    </div>
                )}
            </div>

            {isOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-35 z-50"></div>

                    <div className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full">
                        <div className="relative p-4 w-full max-w-md h-full md:h-auto">
                            <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
                                <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        Select a Provider
                                    </h3>
                                    <button onClick={toggleModal} className="text-gray-400 hover:bg-gray-200 rounded-lg text-sm p-1.5 ml-auto">
                                        {/* Close button icon */}
                                    </button>
                                </div>
                                <div className="p-4">
                                    <input
                                        type="text"
                                        placeholder="Search providers..."
                                        className="mb-4 bg-gray-50 border text-gray-900 text-sm rounded-lg block w-full p-2.5"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    {isLoading ? (
                                        <Spinner minHeight={100} />
                                    ) : (
                                        <ul className="max-h-60 overflow-y-auto">
                                            {availableClients.map((client) => (
                                                <li
                                                    key={client.id}
                                                    onClick={() => handleSelectClient(client)}
                                                    className="cursor-pointer hover:bg-gray-100 p-2"
                                                >
                                                    {client.name}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="flex items-center p-6 space-x-2 rounded-b dark:border-gray-600">
                                    <button onClick={toggleModal} className="bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 text-white font-medium rounded-lg text-sm px-5 py-2.5">
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ClientModal;
